import * as React from 'react';

/**
 * Attaches list of component behaviors to provided HTML element using nested 'with' function
 * @param {JSX.Element} target a target HTML element
 * @param {React.Component<any, any>[]} behaviors list of react component behaviors to attach target with
 * @returns {JSX.Element} element with attched behaviors
 */
export function attachBehaviors(target: JSX.Element, behaviors: JSX.Element[]): JSX.Element {
    return behaviors ? (
        <BehaviorsHelper key={'behaviors_' + target.key} behaviors={behaviors}>
            {target}
        </BehaviorsHelper>
    ) : (
        target
    );
}

interface BehaviorsHelperProps {
    behaviors: JSX.Element[];
}

/**
 * This component gets a list of react components behaviors and target container.
 * The component behaviors may inject content and/or event handlers under the common HTML element
 * The component behavior should implement handlers() method to list event handlers it want to inject
 * Your component is notified on event and may render content which is mounted as a child of container.
 * Examples:
 * 1. Component behavior implements mouseDown event and renders selection area on DIV element
 * 2. Component behavior has no event handlers but injects visual content generated on some parameters
 */
class BehaviorsHelper extends React.Component<BehaviorsHelperProps, {}> {
    private children: any[] = [];
    private handlers: {} = {};
    private behaviorComponents = [];

    render() {
        let handlersSpec = {};

        // Behaviors are wrapped inside React Element and only instantiated when React performs mountying cycle.
        // So for every behavior we have to collect their handlers spec and setup ref callback for getting
        // handler instances later
        this.children = this.props.behaviors.map(behavior => {
            let behaviorObject: Object = (behavior.type as any).prototype;
            let behaviorHandlersSpec = behaviorObject.hasOwnProperty('handlers')
                ? (behavior.type as any).prototype.handlers()
                : null;
            handlersSpec = { ...handlersSpec, ...behaviorHandlersSpec };

            return React.cloneElement(behavior, {
                ...behavior.props,
                ...{
                    key: behavior.key ? behavior.key : 'behaviorKey' + (behavior.type as any).name,
                    ref: behaviorComponent => {
                        if (behaviorComponent) {
                            // Now when we have an instance of Behavior component, update handlers table with real handler.
                            if (behaviorComponent.handlers) {
                                let handlers = behaviorComponent.handlers();
                                if (handlers) {
                                    Object.keys(handlers).forEach(key => {
                                        this.handlers[key] = handlers[key].bind(behaviorComponent);
                                    });
                                }
                            }
                            behaviorComponent.onAttach &&
                                !this.behaviorComponents.some(
                                    component => component === behaviorComponent
                                ) &&
                                this.behaviorComponents.push(behaviorComponent);
                        }
                    },
                },
            });
        });

        // Now create 'this' handlers for all of the collected handlers from provided behaviors
        // 'this' handlers are bound to div and will wrap behaviors handler once we get them
        Object.keys(handlersSpec).forEach(key => {
            // Set this 'this' handler to invoke behavior handler
            this[key] = el => {
                let handler = this.handlers[key];
                if (handler) {
                    this.handlers[key](el);
                }
            };
            handlersSpec[key] = this[key];
        });

        // Clone the child to inject 'this' handlers and add behaviors for injecting content
        // this.children comes before child.props.children in order to force injected behaviors to all come earlier
        // in the DOM than the child, making them sit behind by default. There was a bug in Chrome only where having the
        // TodayHighlight behavior sitting on top of an item was causing drag events to fire unintentionally, so moving it
        // behind fixes that.
        let child = this.props.children as React.ReactElement<any>;
        return React.cloneElement(
            child,
            {
                ...handlersSpec,
                ref: this.childRefFunction(child),
            },
            this.children.concat([child.props.children])
        );
    }

    // use currying to check if the given child has a ref function so we don't destroy it
    private childRefFunction = (child: React.ReactElement<any>) => (el: any) => {
        let existingRefFunction = (child as React.ClassAttributes<HTMLElement>).ref;
        if (existingRefFunction && typeof existingRefFunction == 'function') {
            existingRefFunction(el);
        }

        this.notifyAttach(el);
    };

    private notifyAttach: (el: any) => void = el => {
        if (el) {
            this.behaviorComponents.forEach(behaviorComponent => {
                behaviorComponent.onAttach && behaviorComponent.onAttach(el);
            });
        }
    };
}
